import React, { useMemo, useRef } from "react";
import { ScatterChart } from 'echarts/charts';
import CustomEchartLegend from "./CustomEchartLegend";
import { Row, Col } from "react-bootstrap"
import { ECHARTS_COLORS as COLORS } from '../../constants/echarts-colors.constants';

import { EchartsCore } from "../Echart/UseEcharts";

const round = (value, precision) => {
  let multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

const getCompanySizeLB = (value) => {
  switch (value) {
    case 'HC5S':
    case 'HC10S':
    case 'HCMAX':
      return 100;
    case 'HC1S':
      return 90;
    case 'HC500':
      return 80;
    case 'HC200':
      return 70;
    case 'HC50':
      return 60;
    case 'HC1':
    case 'HC10':
      return 50;
    default:
      return 1;
  }
}

const getCompanyRadiusLB = (value) => {
  switch (value) {
    case 'HC10S':
    case 'HCMAX':
      return 50;
    case 'HC5S':
      return 45;
    case 'HC1S':
      return 40;
    case 'HC500':
      return 35;
    case 'HC200':
      return 30;
    case 'HC50':
      return 25;
    case 'HC1':
    case 'HC10':
      return 20;
    default:
      return 20;
  }
}

const graphicLabelStyle = {
  fill: "#b5bfd1",
  fontSize: 14,
};

const ChartBoard = (props) => {
  const { title, data } = props;

  const ReactEchart = EchartsCore([ScatterChart]);

  const chartRef = useRef(null);

  const maxFocusPercent = Math.max(...data.map(item => item.percent));

  const initData = data.map((item, ind) => ({
    id: ind + 1,
    title: item.title,
    score: {
      overall: item.points,
      focus: item.percent,
      focus_reformed: item.percent === maxFocusPercent ? 7 : round(item.percent * 7 / maxFocusPercent),
      company_size: getCompanySizeLB(item.company_size_value),
      company_radius: getCompanyRadiusLB(item.company_size_value),
    }
  }));

  const gridOptions = useMemo(() => {
    const minYPoints = Math.min(...initData.map(item => item.score.overall)) - 1;
    const maxYPoints = Math.max(...initData.map(item => item.score.overall)) + 1;
    const yAxisCenter = (minYPoints + maxYPoints) / 2;

    const minXPoints = round(Math.min(...initData.map(item => item.score.focus_reformed * item.score.company_size / 100)) - 0.5);
    const maxXPoints = round(Math.max(...initData.map(item => item.score.focus_reformed * item.score.company_size / 100)) + 1);
    const xAxisCenter = (minXPoints + maxXPoints) / 2;

    return {
      minX: minXPoints - xAxisCenter,
      maxX: maxXPoints - xAxisCenter,
      centerX: xAxisCenter,
      minY: minYPoints - yAxisCenter,
      maxY: maxYPoints - yAxisCenter,
      centerY: yAxisCenter,
    }
  }, [initData]);

  const chartData = useMemo(() =>
    initData.map((item) => ([
      item.score.focus_reformed * item.score.company_size / 100 - gridOptions.centerX,
      item.score.overall - gridOptions.centerY,
      item.score.company_radius,
      item.title,
    ])), [gridOptions, initData]);

  const pieces = useMemo(() => chartData.map((item, i) => ({
    value: item[3],
    label: item[3],
    color: COLORS[i],
  })), [chartData]);

  const options = useMemo(() => ({
    xAxis: {
      name: 'OVERALL SCORE ➜',
      nameRotate: 90,
      nameLocation: 'start',
      min: gridOptions.minX,
      max: gridOptions.maxX,
      splitNumber: 6,
      interval: (gridOptions.maxX - gridOptions.minX) / 6,
      axisTick: {
        show: false
      },
      axisLabel: {
        show: false
      },
      axisLine: {
        lineStyle: {
          width: 2
        }
      }
    },
    yAxis: {
      name: 'FOCUS & SIZE ➜',
      nameLocation: 'start',
      min: gridOptions.minY,
      max: gridOptions.maxY,
      splitNumber: 8,
      interval: (gridOptions.maxY - gridOptions.minY) / 8,
      axisTick: {
        show: false
      },
      axisLabel: {
        show: false
      },
      axisLine: {
        lineStyle: {
          width: 2
        }
      }
    },
    series: [
      {
        symbolSize: (value) => value[2],
        data: chartData,
        type: 'scatter',
        itemStyle: {
          opacity: 0.95,
        },
      }
    ],
    grid: {
      left: '40',
      right: '10',
      top: '30',
      bottom: '30',
    },
    visualMap: {
      type: 'piecewise',
      itemWidth: 0,
      showLabel: false,
      pieces: pieces,
    },
    tooltip: {
      position: 'top',
      formatter: function (params) {
        return `${params.dataIndex + 1} ${params.data[3]}`;
      },
    },
    graphic: [
      {
        type: "text",
        left: "42",
        top: "32",
        z: 3,
        silent: true,
        style: {
          text: "CONTENDERS",
          ...graphicLabelStyle
        },
      },
      {
        type: "text",
        left: "42",
        bottom: "32",
        z: 3,
        silent: true,
        style: {
          text: "RISING STARS",
          ...graphicLabelStyle
        },
      },
      {
        type: "text",
        right: "12",
        top: "32",
        z: 3,
        silent: true,
        style: {
          text: "LEADERS",
          ...graphicLabelStyle
        },
      },
      {
        type: "text",
        right: "12",
        bottom: "32",
        silent: true,
        z: 3,
        style: {
          text: "STRONG PLAYERS",
          ...graphicLabelStyle
        },
      },
    ],
  }), [gridOptions]);

  return (
    <section className="py-5">
      <h2 className="mb-4">{title}</h2>
      <div className="p-4 card border-0 rounded-0 shadow-none d-flex flex-column flex-lg-row align-items-center gap-5 gap-lg-2">
        <Col className="col-12 col-lg-5 position-relative">
          <ReactEchart
            lazyUpdate
            opt={{ renderer: 'svg' }}
            option={options}
            ref={chartRef}
            style={{ height: "500px", maxWidth: '520px', width: '100%'}}
          />
        </Col>
        <Col>
          <CustomEchartLegend chartRef={chartRef} dataset={chartData} key={title} />
        </Col>
      </div>
    </section>
  )
}

export default ChartBoard